import { supabase } from "../lib/supabase.js";
import { categoryMeta } from "./categories.js";

/* The product catalogue in Supabase.

   Columns as imported:
     id, category, brand, model, colorway, sku, release_era,
     retail_price_approx, materials, image_url, source

   Note what is not there: a barcode. The catalogue is seed data — every row
   carries source = 'seed' — and seeded rows have no real UPC or EAN behind
   them. That is not a wiring problem, it is the data: a scanner returns the
   number printed on a box, and nothing in this table records that number, so
   no query can connect the two. Scanning therefore cannot match a catalogue
   row until real barcodes exist, and pretending otherwise would just produce
   "no match found" on every scan of a shoe that is plainly in the catalogue.

   Set BARCODE_COLUMN to the column name once barcodes are imported and the
   lookup below starts working with no other change. */
const BARCODE_COLUMN = null;

/* Only what the Add Item form can use. Selecting * would pull every column of
   a 2,140-row catalogue over a phone connection for a single lookup. */
const FIELDS = "id, category, brand, model, colorway, retail_price_approx, materials";

/** Turns a catalogue row into the shape the Add Item form expects.
 *
 *  The two `materials` shapes are not the same: the catalogue stores one value
 *  per slot — {"upper":"Mesh","midsole":"Zoom Air"} — while the form stores a
 *  list per DNA group, because a shoe can have several upper materials. Some
 *  rows carry {"notes":"…"} instead, which is prose and belongs to no group.
 *
 *  So the mapping is deliberately conservative: a value is only applied when
 *  its group exists for that category AND the value is one of that group's
 *  options. Anything else is dropped rather than written into the form, where
 *  it would sit in the record as a material no chip can display or unset. */
export function materialsForForm(category, catalogueMaterials) {
  const groups = categoryMeta(category)?.groups || [];
  const out = {};
  for (const group of groups) {
    const raw = catalogueMaterials?.[group.key];
    if (!raw) continue;
    const values = (Array.isArray(raw) ? raw : [raw]).filter((v) => group.options.includes(v));
    if (values.length) out[group.key] = values;
  }
  return out;
}

/** A catalogue row, in the shape the wizard fills its form from. */
function toProduct(row) {
  return {
    category: row.category || null,
    brand: row.brand || "",
    model: row.model || "",
    colorway: row.colorway || "",
    // The catalogue records an approximate retail only; what a collector paid
    // and what it is worth now are theirs to enter.
    retail: row.retail_price_approx ?? null,
    materials: row.materials || {},
  };
}

/* The same physical barcode has two legitimate spellings: UPC-A is EAN-13 with
   a leading zero, and decoders normalise between them — ZXing reports the
   twelve digit form for a symbol encoding 0884751780851. Whether a catalogue
   stores twelve or thirteen digits is a coin flip, so both are matched. */
export function barcodeCandidates(code) {
  const c = String(code || "").trim();
  if (!c) return [];
  const forms = new Set([c]);
  if (c.length === 12) forms.add("0" + c);
  if (c.length === 13 && c.startsWith("0")) forms.add(c.slice(1));
  return [...forms];
}

/** Looks up one scanned code.
 *
 *  Returns one of:
 *    { unsupported: true }  the catalogue holds no barcodes, so no lookup is
 *                           possible — reported without a pointless round trip
 *    { product }            a hit
 *    { product: null }      a genuine miss
 *    { error }              the lookup itself failed
 *
 *  Those last three are kept apart deliberately. A miss means "not catalogued,
 *  type it in"; a failure means "we don't know". Telling someone their shoe
 *  isn't catalogued because their signal dropped is how a record ends up wrong. */
export async function lookupBarcode(code) {
  if (!BARCODE_COLUMN) return { unsupported: true };

  const forms = barcodeCandidates(code);
  if (!forms.length) return { product: null };

  const { data, error } = await supabase
    .from("products")
    .select(FIELDS)
    .in(BARCODE_COLUMN, forms)
    .limit(1)
    .maybeSingle();

  if (error) return { error: describe(error) };
  return { product: data ? toProduct(data) : null };
}

/* Why a catalogue query failed matters, because the fixes have nothing to do
   with each other: a missing grant is a migration nobody ran, a missing column
   is a schema that moved, a dropped connection is a lift. The screen stays
   plain, but the reason goes to the console so it can be read rather than
   guessed at — this project has already lost time to "couldn't reach the
   catalogue" meaning six different things. */
function describe(error) {
  const code = error?.code || "";
  const hint =
    code === "42501" || /permission denied/i.test(error?.message || "")
      ? "public.products is not readable by signed-in users — apply supabase/migrations/0003_products_read_access.sql."
      : code === "PGRST205" || code === "42P01"
        ? "No public.products table is visible to PostgREST — check the table exists and reload its schema cache."
        : code === "42703"
          ? "The catalogue query asked for a column that does not exist — the table's shape has changed."
          : "The request did not reach the catalogue.";
  console.error(`[catalogue] ${hint}`, error);
  return error;
}

/** Free-text search over the catalogue by brand and model.
 *
 *  This is what the seed data can actually support: no barcodes, but 2,140
 *  rows of brand/model/colorway/materials worth not retyping. */
export async function searchCatalogue(query, limit = 20) {
  const q = String(query || "").trim();
  if (q.length < 2) return { results: [] };

  // Escape the PostgREST or() separators so a comma or paren in the query
  // cannot break out of the filter expression.
  const safe = q.replace(/[,()]/g, " ");
  const { data, error } = await supabase
    .from("products")
    .select(FIELDS)
    .or(`brand.ilike.%${safe}%,model.ilike.%${safe}%`)
    .order("brand")
    .limit(limit);

  if (error) return { error: describe(error) };
  return { results: (data || []).map((row) => ({ id: row.id, ...toProduct(row) })) };
}
